/**
 * Silverback Merchandising — Collection Page Personaliser
 * v1.0 — Reads Klaviyo identity, re-sorts collection grid for known customers
 * Falls back to generic signal-ranked sort for anonymous visitors
 *
 * Install: add before </body> in theme.liquid (after search widget)
 * <script src="https://email.silverbackgymwear.com/talent/public/silverback-merch-v1.js" defer></script>
 */
(function () {
  'use strict';

  const API_BASE = 'https://email.silverbackgymwear.com';

  // ── Only run on collection pages ──────────────────────────────────────────
  const isCollection = /^\/collections\/[^\/]+\/?$/.test(location.pathname);
  if (!isCollection) return;

  // ── Get collection handle from URL ────────────────────────────────────────
  const collectionHandle = location.pathname.replace(/^\/collections\//, '').replace(/\/$/, '');
  if (!collectionHandle || collectionHandle === 'all') return;

  // ── Decode Klaviyo identity cookie ────────────────────────────────────────
  function getKlaviyoEmail() {
    try {
      const raw = document.cookie.split(';').map(c => c.trim())
        .find(c => c.startsWith('__kla_id=') || c.startsWith('_kla_='));
      if (!raw) return null;
      const val = decodeURIComponent(raw.split('=').slice(1).join('='));
      const decoded = JSON.parse(atob(val));
      return decoded?.$email || decoded?.email || null;
    } catch (_) { return null; }
  }

  // ── Find product cards in the collection grid ─────────────────────────────
  const CARD_SELECTORS = [
    '[data-product-id]',
    '[data-id][class*="product"]',
    'li[data-product-handle]',
    '.product-card[data-id]',
    '.grid__item[data-product-id]',
    '.js-product-item',
    'div[data-product-id]',
    'article[data-product-id]',
  ];
  const GRID_SELECTORS = [
    // Concept 5.31 (RoarTheme)
    'motion-list.card-grid',
    '.card-grid',
    '#ProductGridContainer',
    // Legacy / other themes
    '.product-grid',
    '.collection-grid',
    '.products-grid',
    '.grid.product-grid',
    'ul.grid',
    '.js-product-grid',
    '.collection__products',
    '[class*="product-list"]',
  ];

  function findProductCards() {
    // Try grid container first, then fall back to document-wide
    for (const gSel of GRID_SELECTORS) {
      const grid = document.querySelector(gSel);
      if (!grid) continue;
      for (const cSel of CARD_SELECTORS) {
        const cards = [...grid.querySelectorAll(cSel)];
        if (cards.length > 2) return { grid, cards };
      }
    }
    // Last resort: any element with a product-id data attribute
    const fallback = [...document.querySelectorAll('[data-product-id]')].filter(el => el.closest('ul, ol, div, section'));
    if (fallback.length > 2) {
      return { grid: fallback[0].parentElement, cards: fallback };
    }
    return null;
  }

  // ── Extract Shopify product ID from a card element ────────────────────────
  function getProductId(card) {
    const id =
      card.dataset.productId ||
      card.dataset.id ||
      card.querySelector('[data-product-id]')?.dataset?.productId ||
      card.querySelector('a[href*="/products/"]')?.href?.match(/\/products\/([^?#\/]+)/)?.[1];
    return id ? String(id).replace(/\D/g, '') : null;
  }

  // ── Re-order cards in the grid based on sorted ID list ────────────────────
  function reorderGrid(grid, cards, orderedIds) {
    const idToCard = {};
    for (const card of cards) {
      const id = getProductId(card);
      if (id) idToCard[id] = card;
    }

    // Insert in order (only cards we have IDs for)
    let inserted = 0;
    for (const id of orderedIds) {
      const card = idToCard[String(id)];
      if (!card) continue;
      grid.appendChild(card); // moves to end, maintaining order
      inserted++;
    }

    if (inserted > 2) {
      console.debug(`[sb-merch] Reordered ${inserted} products in ${collectionHandle}`);
    }
  }

  // ── Main ──────────────────────────────────────────────────────────────────
  async function run() {
    // Small delay so page renders first (no visible reorder flash for most connections)
    await new Promise(r => setTimeout(r, 80));

    const email = getKlaviyoEmail();

    try {
      const payload = { collection: collectionHandle };
      if (email) payload.email = email;

      const res = await fetch(`${API_BASE}/api/personalise`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });

      if (!res.ok) return;
      const data = await res.json();
      if (!data?.productIds?.length) return;

      // Wait for DOM to be ready (sometimes lazy-loaded grids)
      let found = findProductCards();
      if (!found) {
        await new Promise(r => setTimeout(r, 600));
        found = findProductCards();
      }
      if (!found) return;

      reorderGrid(found.grid, found.cards, data.productIds);

      // Track personalisation event (optional, non-blocking)
      if (email && data.personalised) {
        try {
          fetch(`${API_BASE}/api/search-analytics`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({
              type:       'merch_personalise',
              collection: collectionHandle,
              email:      email,
              ts:         Date.now(),
            }),
          }).catch(() => {});
        } catch (_) {}
      }
    } catch (_) {}
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
